<template>
  <div class="absolute right-0 top-0 z-[100] w-full">
    <!-- Wrapper -->
    <div class="relative h-dvh w-full">
      <!-- Overlay -->
      <div class="absolute left-0 top-0 h-full w-full bg-black/[.2]" />
      <!-- Slider -->
      <div
        class="relative z-[100] flex h-[90%] w-full flex-col bg-white *:box-border 2xl:h-[85%] short:h-full"
      >
        <!-- Header -->
        <section
          id="esg-picker-header"
          class="flex flex-none items-center justify-between border-b border-slate-300 px-4 py-4 sm:px-6 lg:px-8"
        >
          <h2 class="text-lg font-semibold text-primary">
            Select ESG Metrics
          </h2>
          <span
            v-if="steps"
            class="text-sm text-primary"
          >{{ steps }} {{ steps > 1 ? "steps" : "step" }} left</span>
          <div
            v-else
            class="flex items-center gap-2 text-sm text-main-primary"
          >
            <span>Completed</span>
            <CheckCircleIcon custom-style="h-3 w-3" />
          </div>
        </section>
        <!-- Progress -->
        <section
          class="flex flex-none items-center justify-between px-6 pt-4 sm:px-8 lg:px-10"
        >
          <div class="flex items-center gap-x-2">
            <AnimatedPing />
            <h3
              id="esg-picker-progress"
              class="text-sm font-medium text-gray-400"
            >
              {{ progressMessage }}
            </h3>
          </div>
          <RequestEsgFrameworksButton />
        </section>
        <!-- Pickers  -->
        <section
          class="flex min-h-0 grow flex-col gap-2 px-6 pb-4 pt-5 sm:px-8 lg:px-10"
        >
          <div
            class="grid min-h-0 grow grid-cols-3 gap-6 *:h-full *:overflow-y-auto"
          >
            <EsgFrameworksPicker @version-selected="handleVersionSelected" />
            <EsgTopicsPicker
              :theme-topics="frameworkThemeTopics"
              @topics-selected="selectedTopics = $event"
            />
            <EsgMetricsPicker
              :topics-metrics="selectedTopics"
              @metrics-selected="selectedMetrics = $event"
            />
          </div>
          <div class="flex flex-none items-center justify-end gap-2">
            <GhostButton
              classes="!py-2 px-4"
              @click="$emit('hide')"
            >
              Cancel
            </GhostButton>
            <PrimaryButton
              classes="!py-2.5 px-5"
              :disabled="!selectedMetrics.length"
              @click="saveSelectedEsgFrameworkTopicMetric"
            >
              Save
            </PrimaryButton>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ProgressMessages } from '@/utils/esg'

export default {
  name: 'EsgFrameworkTopicMetricPicker',

  emits: ['hide'],

  data() {
    return {
      steps: 3,
      progressMessage: ProgressMessages.Frameworks,
      frameworkThemeTopics: [],
      selectedFrameworks: [],
      selectedTopics: [],
      selectedMetrics: [],
    }
  },

  computed: {
    frameworksAreSelected() {
      return this.frameworkThemeTopics.some(({ theme_items }) => !!theme_items.length)
    },
  },

  watch: {
    frameworkThemeTopics: {
      deep: true,
      handler(newTopics, oldTopics) {
        if (JSON.stringify(newTopics) !== JSON.stringify(oldTopics)) {
          this.progressMessage = this.frameworksAreSelected ? ProgressMessages.TopicsStart : ProgressMessages.Frameworks
        }
      },
    },
    selectedTopics: {
      deep: true,
      handler(newTopics) {
        if (newTopics.length) {
          this.progressMessage = ProgressMessages.MetricsStart
        }
        else if (!this.frameworksAreSelected && !newTopics.length) {
          this.progressMessage = ProgressMessages.Frameworks
        }
        else {
          this.progressMessage = ProgressMessages.TopicsStart
        }
      },
    },
    selectedMetrics: {
      deep: true,
      handler(newMetrics) {
        if (newMetrics.length) {
          this.progressMessage = ProgressMessages.MetricsEnd
        }
        if (!newMetrics.length && !!this.selectedTopics.length) {
          this.progressMessage = ProgressMessages.MetricsStart
        }
      },
    },
    progressMessage(newMessage) {
      switch (newMessage) {
        case ProgressMessages.Frameworks:
          this.steps = 3
          break
        case ProgressMessages.TopicsStart:
          this.steps = 2
          break
        case ProgressMessages.MetricsStart:
          this.steps = 1
          break
        case ProgressMessages.MetricsEnd:
          this.steps = 0
          break
        default:
          this.steps = 3
          break
      }
    },
  },

  methods: {
    handleVersionSelected(event) {
      const themeTopics = [
        { theme: 'Environment', theme_items: [] },
        { theme: 'Social', theme_items: [] },
        { theme: 'Governance', theme_items: [] },
      ]
      this.selectedFrameworks = []
      // TODO: Bad performance here, refactor later
      event.forEach(({ versions }) => {
        versions.forEach(({ isChecked, topics, id }) => {
          if (isChecked) {
            this.selectedFrameworks.push(id)
            topics.forEach(({ theme, items }) => {
              items.forEach((userSelectedItem, topicIndex) => {
                const itemThemeIndex = themeTopics.findIndex(
                  topic => topic.theme === theme,
                )
                const itemThemeTopics = themeTopics[itemThemeIndex].theme_items

                if (
                  itemThemeTopics.find(
                    ({ id }) => id === userSelectedItem.id,
                  ) === undefined
                ) {
                  themeTopics[itemThemeIndex].theme_items.push(
                    userSelectedItem,
                  )
                }
                else {
                  userSelectedItem.metrics.forEach((metric) => {
                    if (
                      themeTopics[itemThemeIndex].theme_items[
                        topicIndex
                      ].metrics.find(
                        ({ metric_id }) => metric_id === metric.metric_id,
                      ) === undefined
                    ) {
                      themeTopics[itemThemeIndex].theme_items[
                        topicIndex
                      ].metrics.push({ ...metric, checked: false })
                    }
                  })
                }
              })
            })
          }
        })
      })
      this.frameworkThemeTopics = themeTopics
    },
    saveSelectedEsgFrameworkTopicMetric() {
      const cid = localStorage.getItem('cid')
      const saveData = {
        templates: this.selectedFrameworks,
        topics: [...this.selectedTopics].map(({ id }) => id),
        metrics: [...this.selectedMetrics].map(({ metric_id }) => metric_id),
      }

      this.$apis.esgMetrics.listAll(cid).then(({ data: esgMetricsData }) => {
        if (esgMetricsData.length) {
          this.$apis.esgMetrics
            .update(esgMetricsData[0].id, cid, saveData)
            .then(({ data }) => this.alertSaveSuccess(data.message))
        }
        else {
          this.$apis.esgMetrics
            .create(cid, saveData)
            .then(({ data }) => this.alertSaveSuccess(data.message))
        }
      })
    },
    alertSaveSuccess(message) {
      this.$toast.success(message, {
        timeout: 2000,
        position: 'top-right',
      })

      this.$emit('hide')
    },
  },
}
</script>
